import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { isValidYoutubeUrl } from '@tiptap/extension-youtube'
import useFloatingPosition from './useFloatingPosition'
import { getPortalContainer } from './portal'
import { uploadFile, fetchLinkPreview } from './api'
import { nodeForFile } from './mediaNodes'
import useStyles from './styles'


const mediaTypes = {
  image: {
    accept: 'image/*',
    uploadLabel: 'Upload image',
    linkLabel: 'Embed image',
    placeholder: 'Paste the image link…',
    fromLink: url => ({ type: 'image', attrs: { src: url } })
  },
  video: {
    accept: 'video/*',
    uploadLabel: 'Upload video',
    linkLabel: 'Embed video',
    placeholder: 'Paste a YouTube or video file link…',
    fromLink: url => (isValidYoutubeUrl(url) ?
      { type: 'youtube', attrs: { src: url } } :
      { type: 'video', attrs: { src: url } })
  },
  audio: {
    accept: 'audio/*',
    uploadLabel: 'Upload audio',
    linkLabel: 'Embed audio',
    placeholder: 'Paste the audio file link…',
    fromLink: url => ({ type: 'audio', attrs: { src: url } })
  },
  file: {
    accept: undefined,
    uploadLabel: 'Upload file',
    linkLabel: 'Link file',
    placeholder: 'Paste the file link…',
    fromLink: url => ({
      type: 'fileAttachment',
      attrs: { href: url, name: decodeURIComponent(new URL(url).pathname.split('/').pop() || url) }
    })
  },
  bookmark: {
    linkLabel: 'Create bookmark',
    placeholder: 'Paste in https://…',
    fromLink: url => fetchLinkPreview(url).then(preview => ({
      type: 'bookmark',
      attrs: {
        url: preview.url || url,
        title: preview.title,
        description: preview.description,
        image: preview.image,
        favicon: preview.favicon
      }
    }))
  }
}

const normalizeUrl = (value) => {
  const trimmed = value.trim()
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(withScheme)
    return ['http:', 'https:'].includes(url.protocol) && url.hostname.includes('.') ? url.href : null
  } catch (error) {
    return null
  }
}

const errorMessage = (error, fallback) => {
  const data = error && error.response && error.response.data
  if (data && typeof data === 'object') {
    const first = Object.values(data)[0]
    return Array.isArray(first) ? first[0] : String(first)
  }
  return fallback
}

// Replaces the empty paragraph left behind by "/image" etc., otherwise
// inserts the block at `position`
export const insertBlockAt = (editor, position, content) => {
  const { doc } = editor.state
  const $pos = doc.resolve(Math.min(position, doc.content.size))
  const target = ($pos.parent.type.name === 'paragraph' && $pos.parent.content.size === 0 && $pos.depth > 0) ?
    { from: $pos.before(), to: $pos.after() } :
    $pos.pos
  editor.chain().focus().insertContentAt(target, content).run()
}

// Upload / embed-link popover opened from the "/" menu's Media items
const MediaPicker = ({ editor, media, onClose }) => {
  const localClasses = useStyles()
  const pickerRef = useRef(null)
  const fileInputRef = useRef(null)
  const config = mediaTypes[media.type]
  const canUpload = !!config.uploadLabel
  const [tab, setTab] = useState(canUpload ? 'upload' : 'link')
  const [link, setLink] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useFloatingPosition(
    pickerRef,
    () => ({ getBoundingClientRect: () => media.rect, contextElement: editor.view.dom }),
    { open: true, placement: 'bottom-start', gap: 8 },
    [media]
  )

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target) && !busy) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [busy, onClose])

  const insert = (content) => {
    insertBlockAt(editor, media.position, content)
    onClose()
  }

  const handleFile = (event) => {
    const file = event.target.files[0]
    if (!file) {
      return
    }
    setBusy(true)
    setError('')
    uploadFile(file)
      .then(uploaded => insert(nodeForFile({ ...uploaded, type: media.type === 'file' ? '' : file.type })))
      .catch((uploadError) => {
        setBusy(false)
        setError(errorMessage(uploadError, 'Upload failed. Please try again.'))
      })
  }

  const handleLink = (event) => {
    event.preventDefault()
    // React bubbles events through portals, so without this the surrounding form (e.g. AddAdvice) submits too
    event.stopPropagation()
    const url = normalizeUrl(link)
    if (!url) {
      setError('Please enter a valid http(s) link.')
      return
    }
    setBusy(true)
    setError('')
    Promise.resolve(config.fromLink(url))
      .then(insert)
      .catch((linkError) => {
        setBusy(false)
        setError(errorMessage(linkError, 'Could not create the bookmark.'))
      })
  }

  return ReactDOM.createPortal(
    <div
      className={`${localClasses.popover} ${localClasses.mediaPicker}`}
      ref={pickerRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) {
          event.stopPropagation()
          onClose()
        }
      }}
    >
      <div className={localClasses.mediaTabs}>
        {
          canUpload &&
          <button type='button' className={localClasses.mediaTab} data-active={tab === 'upload'} onClick={() => setTab('upload')}>
            Upload
          </button>
        }
        <button type='button' className={localClasses.mediaTab} data-active={tab === 'link'} onClick={() => setTab('link')}>
          {media.type === 'bookmark' ? 'Web bookmark' : 'Embed link'}
        </button>
      </div>
      <div className={localClasses.mediaBody}>
        {
          tab === 'upload' ?
          <>
            <input ref={fileInputRef} type='file' accept={config.accept} hidden onChange={handleFile} />
            <button
              type='button'
              autoFocus
              className={localClasses.mediaPrimary}
              disabled={busy}
              onClick={() => fileInputRef.current.click()}
            >
              {busy ? 'Uploading…' : config.uploadLabel}
            </button>
            <div className={localClasses.menuHint}>You can also drag and drop or paste files into the editor.</div>
          </> :
          <form onSubmit={handleLink}>
            <input
              autoFocus
              className={localClasses.linkInput}
              placeholder={config.placeholder}
              value={link}
              disabled={busy}
              onChange={event => setLink(event.target.value)}
            />
            <button type='submit' className={localClasses.mediaPrimary} disabled={busy || !link.trim()}>
              {busy ? (media.type === 'bookmark' ? 'Fetching preview…' : 'Embedding…') : config.linkLabel}
            </button>
          </form>
        }
        {
          error &&
          <div className={localClasses.mediaError}>{error}</div>
        }
      </div>
    </div>,
    getPortalContainer(editor)
  )
}

export default MediaPicker
